import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, UserPlus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/context/AuthContext";
import { formatApiErrorDetail } from "@/lib/api";
import { LOGO_URL } from "@/data/site";

export default function Register() {
  const { register, loginWithGoogle } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: "", email: "", phone: "", password: "" });
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (form.password.length < 6) { toast.error("Password must be at least 6 characters."); return; }
    setBusy(true);
    try {
      await register(form);
      toast.success("Account created! Welcome to EXORA.");
      navigate("/portal");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Registration failed.");
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-4 py-24">
      <div className="w-full max-w-md">
        <div className="bg-white rounded-[2rem] p-8 sm:p-10 border border-black/5 shadow-xl">
          <div className="flex justify-center"><img src={LOGO_URL} alt="EXORA" className="h-12" /></div>
          <div className="text-center mt-6">
            <h1 className="font-heading text-2xl font-extrabold text-brand-green">Create Your Account</h1>
            <p className="text-brand-muted text-sm mt-2">Track requests, download invoices, and book recurring services.</p>
          </div>
          <form onSubmit={submit} className="mt-8 space-y-4">
            <div><Label className="text-brand-charcoal">Full Name</Label><Input data-testid="register-name" value={form.name} onChange={(e) => set("name", e.target.value)} required className="mt-2 h-12 rounded-xl" placeholder="Jane Doe" /></div>
            <div><Label className="text-brand-charcoal">Email</Label><Input data-testid="register-email" type="email" value={form.email} onChange={(e) => set("email", e.target.value)} required className="mt-2 h-12 rounded-xl" placeholder="you@email.com" /></div>
            <div><Label className="text-brand-charcoal">Phone</Label><Input data-testid="register-phone" value={form.phone} onChange={(e) => set("phone", e.target.value)} className="mt-2 h-12 rounded-xl" placeholder="(555) 123-4567" /></div>
            <div><Label className="text-brand-charcoal">Password</Label><Input data-testid="register-password" type="password" value={form.password} onChange={(e) => set("password", e.target.value)} required className="mt-2 h-12 rounded-xl" placeholder="At least 6 characters" /></div>
            <Button data-testid="register-submit-btn" type="submit" disabled={busy} className="w-full h-12 rounded-full bg-brand-green text-white font-bold hover:bg-brand-greenHover">
              {busy ? <><Loader2 className="h-5 w-5 mr-2 animate-spin" /> Creating...</> : <><UserPlus className="h-4 w-4 mr-2" /> Create Account</>}
            </Button>
          </form>
          <div className="flex items-center gap-3 my-6">
            <span className="h-px flex-1 bg-black/10" />
            <span className="text-xs text-brand-muted uppercase tracking-widest">or</span>
            <span className="h-px flex-1 bg-black/10" />
          </div>
          <Button type="button" data-testid="google-register-btn" onClick={loginWithGoogle} variant="outline"
            className="w-full h-12 rounded-full border-black/10 font-semibold text-brand-charcoal hover:bg-brand-light">
            <img src="https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/google.svg" alt="" className="h-5 w-5 mr-2" />
            Continue with Google
          </Button>
          <p className="text-center text-sm text-brand-muted mt-6">Already have an account? <Link to="/login" className="text-brand-green font-semibold hover:text-brand-gold">Sign in</Link></p>
        </div>
      </div>
    </div>
  );
}
